import React, { useState } from 'react';
import { useSubscription } from '../context/SubscriptionContext';
import { useLocale } from '../context/LocaleContext';
import { 
  Sparkles, 
  Check, 
  X, 
  Clock, 
  Shield, 
  Zap,
  Heart,
  Brain,
  Bell
} from 'lucide-react';
import PaymentMethodBadges from './PaymentMethodBadges';

/**
 * 🚀 Subscription Paywall - Clean, non-aggressive upgrade prompt
 * 
 * Shows ONLY when:
 * - Trial has ≤2 days remaining (soft prompt)
 * - Trial expired (blocking for Pro features)
 * - User attempts Pro feature after trial
 * 
 * Key features:
 * - Regional pricing (auto-derived)
 * - Currency not selectable by user
 * - Monthly and Annual plans
 * - Clear value proposition
 * - Easy dismiss (except when blocking)
 */
const SubscriptionPaywall = () => {
  const { 
    showPaywall, 
    paywallTrigger, 
    dismissPaywall, 
    startCheckout,
    trialStatus 
  } = useSubscription();
  
  const { t, formatPrice, pricing, locale } = useLocale();
  
  // Safe format function
  const safeFormatPrice = (price) => {
    if (formatPrice) return formatPrice(price);
    return `$${price}`;
  };
  
  const [selectedPlan, setSelectedPlan] = useState('monthly');
  const [processing, setProcessing] = useState(false);

  if (!showPaywall) return null;

  const handleCheckout = async () => {
    setProcessing(true);
    const success = await startCheckout(selectedPlan);
    if (!success) {
      setProcessing(false);
      alert('Error starting checkout. Please try again.');
    }
  };

  // Can dismiss unless trial is expired
  const canDismiss = !trialStatus.isExpired;

  // Determine header based on trigger
  const getHeader = () => {
    switch (paywallTrigger) {
      case 'expired':
        return {
          icon: Clock,
          title: 'Your trial has ended',
          subtitle: 'Upgrade to keep using Pro features'
        };
      case 'expiring_soon':
        return {
          icon: Sparkles,
          title: `${trialStatus.daysRemaining} day${trialStatus.daysRemaining !== 1 ? 's' : ''} left`,
          subtitle: 'Lock in your Pro access today'
        };
      case 'pro_feature':
        return {
          icon: Zap,
          title: 'Pro feature',
          subtitle: 'Upgrade to unlock all features'
        };
      default:
        return {
          icon: Heart,
          title: 'Upgrade to Pro',
          subtitle: 'Get the full LifeFlo experience'
        };
    }
  };

  const header = getHeader();
  const HeaderIcon = header.icon;

  // Get pricing - use locale pricing (already regionally adjusted)
  const monthlyPrice = pricing?.monthly || 9.99;
  const yearlyPrice = pricing?.yearly || 69.99;
  const savingsPercent = pricing?.savingsPercent || Math.round((1 - (yearlyPrice / (monthlyPrice * 12))) * 100);
  const monthlyFromYearly = (yearlyPrice / 12).toFixed(2);
  const taxInclusive = pricing?.taxInclusive || false;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl max-w-md w-full shadow-2xl animate-scale-in overflow-hidden">
        
        {/* Header */}
        <div className="bg-gradient-to-br from-teal-600 to-cyan-500 p-6 text-white text-center relative">
          {canDismiss && (
            <button
              onClick={dismissPaywall}
              className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
          
          <div className="w-14 h-14 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-3">
            <HeaderIcon className="w-7 h-7 text-white" />
          </div>
          
          <h2 className="text-xl font-bold mb-1">{header.title}</h2>
          <p className="text-white/80 text-sm">{header.subtitle}</p>
        </div>

        {/* Plans */}
        <div className="p-6">
          <div className="space-y-3 mb-6">
            {/* Monthly Plan */}
            <button
              onClick={() => setSelectedPlan('monthly')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                selectedPlan === 'monthly'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-teal-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">Monthly</span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">Billed monthly</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-800">
                    {safeFormatPrice(monthlyPrice)}
                  </p>
                  <p className="text-xs text-gray-400">/month</p>
                </div>
              </div>
            </button>

            {/* Annual Plan */}
            <button
              onClick={() => setSelectedPlan('annual')}
              className={`w-full p-4 rounded-xl border-2 text-left transition-all ${
                selectedPlan === 'annual'
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-teal-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-800">Annual</span>
                    <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs rounded-full font-medium">
                      Save {savingsPercent}%
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {safeFormatPrice(monthlyFromYearly)}/mo, billed yearly
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-gray-800">
                    {safeFormatPrice(yearlyPrice)}
                  </p>
                  <p className="text-xs text-gray-400">/year</p>
                </div>
              </div>
            </button>
          </div>

          {/* Features */}
          <div className="bg-gray-50 rounded-xl p-4 mb-6">
            <p className="text-xs font-medium text-gray-600 mb-3">Everything in Pro:</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                { icon: Brain, text: 'AI Memory' },
                { icon: Bell, text: 'Smart Nudges' },
                { icon: Heart, text: 'Health Tracking' },
                { icon: Shield, text: 'Data Backup' }
              ].map((feature, i) => (
                <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                  <feature.icon className="w-3.5 h-3.5 text-teal-500" />
                  <span>{feature.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <button
            onClick={handleCheckout}
            disabled={processing}
            className={`w-full py-3.5 rounded-xl font-medium text-white transition-all ${
              processing
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-gradient-to-r from-teal-600 to-cyan-500 hover:from-teal-700 hover:to-cyan-600 shadow-lg shadow-teal-500/25'
            }`}
          >
            {processing ? (
              <span className="flex items-center justify-center gap-2">
                <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Redirecting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                <Sparkles className="w-4 h-4" />
                Upgrade Now
              </span>
            )}
          </button>

          {/* Trust indicators */}
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-gray-400">
            <span className="flex items-center gap-1">
              <Check className="w-3.5 h-3.5" />
              Cancel anytime
            </span>
            <span className="flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" />
              Secure checkout
            </span>
          </div>
          
          {/* Tax inclusive indicator for EU/UK/AU */}
          {taxInclusive && (
            <p className="text-xs text-gray-400 text-center mt-2">
              Prices include applicable taxes
            </p>
          )}
          
          {/* Regional Payment Methods */}
          {pricing?.payment_methods && pricing.payment_methods.length > 0 && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center mb-2">Available payment methods</p>
              <PaymentMethodBadges methods={pricing.payment_methods} compact={true} className="justify-center" />
            </div>
          )}

          {/* Maybe later (only if can dismiss) */}
          {canDismiss && (
            <button
              onClick={dismissPaywall}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-4 py-2"
            >
              {t('settings.maybeLater') || 'Maybe later'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPaywall;
